<template>
    <div id="app" class="min-h-screen flex flex-col overflow-x-clip max-w-full">
        <!-- 加载动画 -->
        <transition name="fade">
            <div v-if="isLoading" class="fixed inset-0 bg-white z-[100] flex items-center justify-center">
                <div class="text-center">
                    <div
                        class="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto">
                    </div>
                    <p class="mt-4 text-gray-600">{{ $t('加载中...') }}</p>
                </div>
            </div>
        </transition>

        <!-- 路由视图 -->
        <transition
            :name="pageTransitionName"
            :mode="pageTransitionMode"
            @before-enter="beforeEnter"
            @after-enter="afterEnter">
            <router-view v-if="!isLoading" />
        </transition>

        <float-right :routeName="routeName" v-if="is_cart"></float-right>
    </div>
</template>

<script>
    import floatRight from "@/components/floatRight";

    export default {
        components: {
            "float-right": floatRight,
        },
        name: "App",
        data() {
            return {
                isLoading: true,
                transitionName: "fade",
                is_cart: false,
                routeName: "",
            };
        },
        computed: {
            // 进入/离开登录、注册、忘记密码页时关闭过渡（由路由守卫写入 meta.disableTransition）
            pageTransitionEnabled() {
                return !this.$route.meta.disableTransition;
            },
            pageTransitionName() {
                return this.pageTransitionEnabled ? this.transitionName : "";
            },
            pageTransitionMode() {
                return this.pageTransitionEnabled ? "out-in" : undefined;
            },
        },
        watch: {
            $route(to) {
                this.routeName = to.name;
                let name = to.name;
                if (
                    name == "diwaStore" ||
                    name == "searchResult" ||
                    name == "commodityDetail" ||
                    name == "textbookPurchase"
                ) {
                    this.is_cart = true;
                } else {
                    this.is_cart = false;
                }

                // 根据路由meta设置过渡动画
                if (to.meta.transition) {
                    this.transitionName = to.meta.transition;
                } else {
                    this.transitionName = "fade";
                }
            },
            // 登录失效 / 需要登录时跳转登录页
            "$store.state.loginShow"(show) {
                if (show && this.$route.path !== "/register") {
                    this.$store.commit("SET_LOGIN_SHOW", false);
                    this.$router.push({ path: "/register" }).catch(() => {});
                }
            },
        },
        mounted() {
            // 模拟加载完成
            setTimeout(() => {
                this.isLoading = false;
            }, 800);

            // 监听窗口变化
            this.handleResize();
            window.addEventListener("resize", this.handleResize);
        },
        beforeDestroy() {
            window.removeEventListener("resize", this.handleResize);
        },
        methods: {
            handleResize() {
                const width = window.innerWidth;
                if (this.$store && this.$store.state.windowWidth !== width) {
                    this.$store.commit("SET_WINDOW_WIDTH", width);
                }
            },
            beforeEnter(el) {
                if (!this.pageTransitionEnabled) {
                    el.style.removeProperty("opacity");
                    el.style.removeProperty("transform");
                    return;
                }
                el.style.opacity = 0;
                el.style.transform = "translateY(20px)";
            },
            afterEnter(el) {
                el.style.opacity = 1;
                // 勿保留 transform（含 translateY(0)），否则子树内 position:sticky 无法相对视口固定
                el.style.removeProperty('transform');
            },
        },
    };
</script>

<style>
    /* 页面过渡动画 */
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }

    .slide-enter-active,
    .slide-leave-active {
        transition: all 0.4s ease;
    }

    .slide-enter {
        opacity: 0;
        transform: translateX(30px);
    }

    .slide-leave-to {
        opacity: 0;
        transform: translateX(-30px);
    }

    .page-transition {
        animation: pageFade 0.3s ease;
    }

    @keyframes pageFade {
        from {
            opacity: 0;
        }

        to {
            opacity: 1;
        }
    }

    /* 针对所有 input 类型（文本、密码、搜索等） */
    input:focus {
        /* 核心：去掉默认轮廓线 */
        outline: none;
        /* 可选：去掉部分浏览器的焦点阴影 */
        box-shadow: none;
        /* 可选：重置焦点时的边框样式（按需调整） */
        border: none;
        /* 替换为你想要的边框样式 */
    }

    /* 拓展：兼顾 textarea、select 等表单元素 */
    textarea:focus,
    select:focus {
        outline: none;
        box-shadow: none;
    }

    .scroll-box {
        /* 核心：允许滚动 */
        overflow: auto;
        /* Webkit内核 */
        scrollbar-width: none;
        /* Firefox */
        -ms-overflow-style: none;
        /* IE/Edge */
    }

    /* 目标容器样式：保留滚动 + 基础布局 */
    .scroll-box {
        /* 核心：允许滚动（垂直+水平），按需改为overflow-y: auto仅垂直滚动 */
        overflow: auto;
        /* 可选：容器基础样式（根据你的布局调整） */
        width: 300px;
        height: 400px;

        /* 兼容Firefox：隐藏滚动条 */
        scrollbar-width: none;
        /* 兼容IE/Edge旧版：隐藏滚动条 */
        -ms-overflow-style: none;
    }

    /* ========== Webkit内核（Chrome/Edge/Safari）专属配置 ========== */
    /* 1. 隐藏默认滚动条（核心） */
    .scroll-box::-webkit-scrollbar {
        width: 6px;
        /* 垂直滚动条宽度（你要求的6px） */
        height: 6px;
        /* 水平滚动条高度（可选，同宽度） */
        display: none;
        /* 隐藏滚动条（关键：保留滚动功能） */
    }

    /* 2. 若需「取消隐藏时」的滚动条样式（可选，备用） */
    /* 取消隐藏只需注释掉上面的display: none，以下样式会生效 */
    .scroll-box::-webkit-scrollbar-track {
        background: #f5f5f5;
        /* 滚动条轨道背景 */
        border-radius: 3px;
        /* 轨道圆角，匹配6px宽度更美观 */
    }

    .scroll-box::-webkit-scrollbar-thumb {
        background: #ddd;
        /* 滚动条滑块颜色 */
        border-radius: 3px;
        /* 滑块圆角 */
    }

    .scroll-box::-webkit-scrollbar-thumb {
        background: #999;
        /* 鼠标悬浮滑块颜色 */
    }

    /* 可选：临时显示滚动条的hover效果（按需添加） */
    .scroll-box::-webkit-scrollbar {
        display: block;
        /* 鼠标悬浮容器时显示6px宽的自定义滚动条 */
    }

    /* 扩展美洽桌面端聊天窗，移动端仍由插件自身的响应式样式控制。 */
    @media (min-width: 768px) {
        #MEIQIA-PANEL-HOLDER {
            width: 420px !important;
            max-width: calc(100vw - 48px) !important;
        }

        #MEIQIA-PANEL-HOLDER iframe {
            width: 100% !important;
        }
    }
</style>
